import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { I } from './icons';

/* Попапы поверх экрана живут в .dev (как в прототипе): лист снизу .shw,
   окно выбора даты .calw, тост. Экран под ними не перерисовывается. */

const host = () => document.getElementById('dev') || document.body;

/** Держит узел смонтированным ещё ms после закрытия — для анимации ухода (.out) */
export function useLeave(open: boolean, ms = 220) {
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) setShown(true);
    else if (shown) {
      const t = setTimeout(() => setShown(false), ms);
      return () => clearTimeout(t);
    }
  }, [open, shown, ms]);
  return { mounted: open || shown, out: !open && shown };
}

/** Лист снизу: затемнение, ручка, содержимое. Закрывается тапом по затемнению */
export function Sheet({ open, onClose, cls, label, role = 'dialog', children }: { open: boolean; onClose: () => void; cls?: string; label: string; role?: string; children: ReactNode }) {
  const { mounted, out } = useLeave(open);
  if (!mounted) return null;
  return createPortal(
    <div className={'shw' + (out ? ' out' : '')}>
      <div className="shbg" onClick={onClose} />
      <div className={'sheet' + (cls ? ' ' + cls : '')} role={role} aria-label={label}>
        <i className="grab" />
        {children}
      </div>
    </div>,
    host(),
  );
}

/** Лист с формой: «Отмена — заголовок — Сохранить», строки glist */
/* «Сохранить» срабатывает с первого нажатия: mousedown не уводит фокус из поля — иначе на iPhone первое
   нажатие только прячет клавиатуру, лист сдвигается и клик проходит мимо. Пока сохраняется — второй раз не жмётся. */
export function FormSheet({ open, onClose, title, ok, onSave, children, after }: { open: boolean; onClose: () => void; title: string; ok: boolean; onSave: () => void | Promise<unknown>; children: ReactNode; after?: ReactNode }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try {
      await onSave();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} cls="fsheet" label={title}>
      <div className="fsh">
        <button className="lnk" onClick={onClose}>
          Отмена
        </button>
        <b>{title}</b>
        <button className="lnk acc" disabled={!ok || busy} onMouseDown={(e) => e.preventDefault()} onClick={go}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
      <div className="glist">{children}</div>
      {after}
    </Sheet>
  );
}

/** Выбор одного из многих (>10) — лист с поиском вместо системного select, в длинном
    списке (упражнения и т. п.) иначе не найти нужное. Короткие списки — обычный select. */
export function PickSheet({ open, onClose, title, options, value, onPick }: { open: boolean; onClose: () => void; title: string; options: [string, string][]; value: string; onPick: (k: string) => void }) {
  const [q, setQ] = useState('');
  useEffect(() => {
    if (open) setQ('');
  }, [open]);
  const n = q.trim().toLowerCase();
  const list = n ? options.filter(([, label]) => label.toLowerCase().includes(n)) : options;
  return (
    <Sheet open={open} onClose={onClose} cls="fsheet pksheet" label={title}>
      <div className="fsh">
        <button className="lnk" onClick={onClose}>
          Отмена
        </button>
        <b>{title}</b>
        <span />
      </div>
      <div className="pk-s">
        <I n="search" />
        <input autoFocus placeholder="Поиск…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="pk-l">
        {list.map(([k, label]) => (
          <button
            key={k}
            className={'pk-r' + (k === value ? ' on' : '')}
            onClick={() => {
              onPick(k);
              onClose();
            }}
          >
            <span>{label}</span>
            {k === value && <I n="check" />}
          </button>
        ))}
        {!list.length && <div className="pk-e">Ничего не нашли</div>}
      </div>
    </Sheet>
  );
}

/** Подтверждение необратимого: одна красная кнопка */
export function Ask({ open, onClose, title, text, label, onOk }: { open: boolean; onClose: () => void; title: string; text: string; label: string; onOk: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} cls="ask" label={title} role="alertdialog">
      <b>{title}</b>
      <p>{text}</p>
      <button className="btn rmb" onClick={onOk}>
        {label}
      </button>
      <button className="lnk" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  );
}

/* ── тост: одна строка снизу на 2,5 с ── */
let toastState: { t: string; id: number; out: boolean } | null = null;
const toastSubs = new Set<() => void>();
const emit = () => toastSubs.forEach((f) => f());
let seq = 0;
export function toast(t: string) {
  const id = ++seq;
  toastState = { t, id, out: false };
  emit();
  setTimeout(() => {
    if (toastState?.id === id) {
      toastState = { ...toastState, out: true };
      emit();
    }
  }, 2200);
  setTimeout(() => {
    if (toastState?.id === id) {
      toastState = null;
      emit();
    }
  }, 2500);
}
export function Toasts() {
  const s = useSyncExternalStore(
    (f) => {
      toastSubs.add(f);
      return () => toastSubs.delete(f);
    },
    () => toastState,
  );
  if (!s) return null;
  return (
    <div key={s.id} className={'toast' + (s.out ? ' out' : '')} role="status">
      {s.t}
    </div>
  );
}
